import env from "./env";

const getToken = () => localStorage.getItem("token");

const postJson = async (path, data) => {
  const response = await fetch(`${env.url}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  let body;
  try {
    body = await response.json();
  } catch {
    body = {};
  }

  if (!response.ok || body?.success === false) {
    const validationMessage = body?.errors
      ? Object.values(body.errors).flat().join(" ")
      : "";
    throw new Error(validationMessage || body?.message || "Request failed. Please try again.");
  }

  return body;
};

const authenticatedPost = async (path, data, idempotencyKey) => {
  const token = getToken();
  const response = await fetch(env.url + path, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
    },
    body: JSON.stringify(data),
  });
  const body = await response.json();
  if (!response.ok || body?.success === false) {
    const validationMessage = body?.errors
      ? Object.values(body.errors).flat().join(" ")
      : "";
    throw new Error(validationMessage || body?.message || "Request failed. Please try again.");
  }
  return body;
};

const Post = {
  Register: (data) => postJson("/register", data),

  Login: (data) => postJson("/login", data),

  Logout: () => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/logout", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  // Provider & Wallet
  SyncProviderCatalog: () => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/provider/catalog/sync", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  CreateDepositAddress: (data) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/wallets/deposit-address", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  ClaimDeposit: (data) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/wallets/deposit/claim", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  RequestWithdrawal: (data) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/wallets/withdraw", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  CreateInternalTransfer: (data) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/wallets/transfers", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })
        .then(async (response) => {
          const body = await response.json();
          resolve(response.ok || body?.success === false ? body : { ...body, success: false });
        })
        .catch((err) => reject(err));
    });
  },

  MonthlyRewardPreview: (data) => authenticatedPost("/monthly-reward/preview", data),
  EnrollMonthlyReward: (data, idempotencyKey) => authenticatedPost("/monthly-reward/plans", data, idempotencyKey),
  ScipPreview: (data) => authenticatedPost("/scip/preview", data),
  EnrollScip: (data, idempotencyKey) => authenticatedPost("/scip/plans", data, idempotencyKey),
  SuperStakePreview: (data) => authenticatedPost("/super-stake/preview", data),
  CreateSuperStake: (data, idempotencyKey) => authenticatedPost("/super-stake/stakes", data, idempotencyKey),

  // Broker Trading
  CreateBrokerOrder: (data) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/broker/orders", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  CancelBrokerOrder: (orderId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/broker/orders/" + orderId + "/cancel", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  // Internal Exchange
  CreateInternalOrder: (data) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/orders", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  CancelInternalOrder: (orderId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/orders/" + orderId + "/cancel", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  // P2P Ads
  CreateAd: (data) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/ads", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  UpdateAd: (adId, data) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/ads/" + adId, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  DeleteAd: (adId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/ads/" + adId, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  CreateTradeFromAd: (adId, data) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/ads/" + adId + "/trade", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  // P2P Trades
  UpdateP2pTrade: (tradeId, data) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/p2p-trades/" + tradeId, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  MarkP2pTradePaid: (tradeId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/p2p-trades/" + tradeId, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ status: "paid" }),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  ReleaseP2pTrade: (tradeId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/p2p-trades/" + tradeId, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ status: "released" }),
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },

  CancelP2pTrade: (tradeId) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      fetch(env.url + "/p2p-trades/" + tradeId, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  },
};

export default Post;
